// ./src/components/features/EventSetup/useEventTestRecords.ts
//
// ROADMAP #188 — the event's test records (the API's `eventTestRecords`): the current run's while it is in test mode, the
// hidden ones once it is live. Read by the dashboard's test-mode banner (places used, "See test records") and by Details
// & Settings (the hidden ones, "Show" / clear).
//
// ⚠ `settled` says the read for THIS event answered — OK or not. `records` null with `settled` true is a FAILED read, not
//   "no test records": each caller decides what a failure means on its screen.
// ⓘ Kept on screen through a reload (no flicker), and only ever used for the event it was read for. The server answers
//   only someone who may change the event's settings — callers pass `enabled` false for anyone else.
import { useEffect, useState } from 'react';
import { OP_RESULT_CODES } from '@sdflc/api-helpers';

import { EventService, EventTestRecords } from '@/utils/api';

export interface UseEventTestRecordsReturn {
  /** The answer for this event; null before it, or when the read failed. */
  records: EventTestRecords | null;
  /** A read for this event has answered (or failed). */
  settled: boolean;
}

type Read = { eventId: string; records: EventTestRecords | null };

/** `enabled` — read once it is true; `reloadKey` reads again (after a clear, a switch, a refresh). */
export const useEventTestRecords = (eventId: string | null | undefined, enabled: boolean, reloadKey?: number): UseEventTestRecordsReturn => {
  const [read, setRead] = useState<Read | null>(null);

  useEffect(() => {
    if (!enabled || !eventId) {
      return;
    }

    let alive = true;

    void EventService.testRecords(eventId)
      .then((res) => {
        if (alive) {
          setRead({ eventId, records: res.code === OP_RESULT_CODES.OK ? ((res.getDataFirst() as EventTestRecords | undefined) ?? null) : null });
        }
      })
      .catch(() => {
        if (alive) {
          setRead({ eventId, records: null });
        }
      });

    return () => {
      alive = false;
    };
  }, [eventId, enabled, reloadKey]);

  const mine = !!eventId && read?.eventId === eventId;
  const records = mine && read?.records?.eventId === eventId ? (read?.records ?? null) : null;

  return { records, settled: mine };
};
